import * as React from 'react';
import styled, { FlattenInterpolation } from 'styled-components';
import {
  space,
  fontSize,
  color,
  fontWeight,
  SpaceProps,
  FontSizeProps,
  ColorProps,
  WidthProps,
  TextAlignProps,
  LineHeightProps,
  DisplayProps,
  width,
  textAlign,
  lineHeight,
  display,
} from 'styled-system';

import { themeGet } from '../../theme/utils';
import { ThemeColors, FontFamilies, FontWeights } from '../../theme/types';
import { theme } from '../../theme/theme';

type TextVariants = 'p' | 'strong' | 'span' | 'h1' | 'h2' | 'h3' | 'h4';

interface TextBaseStyledProps
  extends SpaceProps,
    FontSizeProps,
    ColorProps,
    WidthProps,
    TextAlignProps,
    LineHeightProps,
    DisplayProps {
  fontFamily?: FontFamilies;
  fontWeight?: FontWeights;
  color?: ThemeColors;
}

const TextBase = styled.p<TextBaseStyledProps & { customCss?: FlattenInterpolation<any> }>`
  ${width}
  ${space}
  ${fontSize}
  ${color}
  ${fontWeight}
  ${width}
  ${textAlign}
  ${lineHeight}
  ${display}
  font-family: ${({ fontFamily = 'brand' }) => themeGet.fonts(fontFamily)};
  font-weight: ${({ fontWeight = 'normal' }) => themeGet.fontWeight(fontWeight)};
  color: ${({ color = 'kilamanjaro' }) => themeGet.colors(color)};
  ${(props) => props.customCss}
`;

const defaultHeadingProps: TextBaseStyledProps = {
  m: 0,
  mt: [3, 3, 4],
  mb: [3, 3, 4],
  pl: [1, 3],
  pr: [1, 3],
  fontSize: [8, 8, 12, 15],
  fontWeight: theme.fontWeights['normal'] as FontWeights,
  fontFamily: theme.fonts['brandSecondary'] as FontFamilies,
  color: theme.fonts['kilamanjaro'] as ThemeColors,
};

const defaultStyledPropsMap: { [key in TextVariants]: TextBaseStyledProps } = {
  p: {
    m: 0,
    pl: [2, 3],
    pr: [2, 3],
    fontSize: [3],
    fontWeight: theme.fontWeights['normal'] as FontWeights,
    fontFamily: theme.fonts['brand'] as FontFamilies,
    color: theme.fonts['kilamanjaro'] as ThemeColors,
  },
  span: {},
  strong: {},
  h1: {
    ...defaultHeadingProps,
  },
  h2: {
    ...defaultHeadingProps,
    fontSize: [6, 8],
  },
  h3: {
    ...defaultHeadingProps,
    fontSize: [2, 3, 4, 5, 6],
  },
  h4: {
    ...defaultHeadingProps,
    fontSize: [6, 6, 6, 6, 8],
  },
};

export interface TextProps extends TextBaseStyledProps {
  tag: TextVariants;
  className?: string;
  customCss?: FlattenInterpolation<any>;
  children?: string | any;
}

export const Text: React.FC<TextProps> = ({ children, className, tag = 'p', customCss, ...styledProps }) => {
  const { color, ...defaultStyledProps } = {
    ...defaultStyledPropsMap[tag],
    ...styledProps,
  };

  // TODO: this is related to this `dangerouslySetInnerHTML` below. Kick this
  // out as soon as <Translate /> is enhanced and can return nodes instead of
  // plain text
  const [content, html] = typeof children !== 'string' ? [children, undefined] : [null, { __html: children }];
  return (
    <TextBase
      as={tag}
      className={className}
      color={color as any}
      customCss={customCss}
      {...{ ...defaultStyledProps }}
      // TODO: as soon as our translation system is able to spit out ReactNodes
      // instead of plain strings this should be removed
      dangerouslySetInnerHTML={html}
    >
      {content}
    </TextBase>
  );
};
